'use client';

import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { navOwner } from './nav-items';

// Pages reached from the account menu rather than the navigation.
const ACCOUNT_PAGES: Readonly<Record<string, string>> = { '/dashboard/profile': 'Profile' };

// Phones keep a 56px header because they need a way into the navigation: the menu and where you are.
// Laptops and tablets have no top bar (docs/design/ui-rules.md, Shell).
export function MobileHeader() {
  const pathname = usePathname();
  const { toggleSidebar, openMobile } = useSidebar();
  const title = navOwner(pathname)?.title ?? ACCOUNT_PAGES[pathname] ?? 'VoodPortal';
  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-1 border-b bg-background px-2 md:hidden">
      <Button variant="ghost" size="icon" className="size-10" onClick={toggleSidebar} aria-label="Open navigation" aria-expanded={openMobile}>
        <Menu className="size-5" aria-hidden="true" />
      </Button>
      {/* The count sits on the title's baseline. */}
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5 pl-0.5">
        <span className="min-w-0 truncate text-[17px] font-semibold leading-[22px]">{title}</span>
        <span id={SLOT_ID} className="flex min-w-0 items-baseline gap-1" />
      </span>
      {/* Lines up with the page content's 16px edge. */}
      <span id={ACTION_SLOT_ID} className="mr-2 flex shrink-0 items-center empty:mr-0" />
    </header>
  );
}

const SLOT_ID = 'mobile-header-slot';
const ACTION_SLOT_ID = 'mobile-header-action';
const noSubscribe = () => () => {};

/**
 * Puts a page's small header extra beside the phone header's title (a count), or at its right
 * end (`action`: the page's primary action).
 */
export function MobileHeaderSlot({ children, action = false }: { children: ReactNode; action?: boolean }) {
  // The header is in the layout, so its slots exist whenever a page renders on the client.
  const id = action ? ACTION_SLOT_ID : SLOT_ID;
  const target = useSyncExternalStore(noSubscribe, () => document.getElementById(id), () => null);
  return target ? createPortal(children, target) : null;
}
